import styled from '@emotion/styled'
import cx from 'classnames'
import { useState } from 'react'

import type { BackgroundColor } from '@/components/Container'
import { Icon } from '@/components/Icon'
import { BodyM } from '@/components/Typography'
import { useSelectCardEvent } from '@/core/analytics/hooks'
import { toKebabCase } from '@/core/utils/dom/string'

const AccordionStyles = styled.li<{ isActive: boolean }>(({ isActive }) => ({
  borderTop: '1px solid #D4DAE0',
  '& .accordion-header': {
    background: 'transparent',
    padding: '1.5rem 0 ',
    border: 'none',
    width: '100%',
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
    cursor: 'pointer',
  },
  '& .accordion-icon': {
    transition: 'all .6s',

    ...(isActive && {
      transform: 'rotate(90deg)',
    }),
  },
  '& .accordion-header:hover .accordion-icon': {
    opacity: '.7',
  },
  '& .accordion-content-wrapper': {
    overflow: 'hidden',
    maxHeight: '0vh',
    opacity: '0',
    willChange: 'max-height',
    transition: 'max-height .6s, opacity .6s',

    ...(isActive && {
      maxHeight: 'auto',
      opacity: '1',
    }),
  },
  '& .accordion-content': {
    padding: '1rem',
  },
}))

interface AccordionProps {
  title: string
  content: string
  className?: string
  lightText?: 'solid'
  background?: BackgroundColor
}

const Accordion = ({ title, content, className, lightText, background }: AccordionProps) => {
  const [isActive, setIsActive] = useState(false)
  const selectContentEvent = useSelectCardEvent()
  const primaryOrCustomBackground = background && ['primary', 'custom'].includes(background)
  const uid = toKebabCase(title).slice(0, 50)
  const headerId = `ah-${uid}`
  const contentId = `ac-${uid}`

  const handleClick = () => {
    selectContentEvent({ label: `perguntas-${title}-${isActive ? 'fechar' : 'abrir'}` })
    setIsActive(!isActive)
  }

  return (
    <AccordionStyles className={cx('accordion', className)} isActive={isActive}>
      <BodyM
        light={lightText}
        dark="high"
        tag="button"
        className="accordion-header"
        id={headerId}
        aria-expanded={isActive}
        aria-controls={contentId}
        onClick={handleClick}
      >
        <Icon
          className={cx('accordion-icon', {
            'et-snow': primaryOrCustomBackground && lightText == 'solid',
            'et-primary': !primaryOrCustomBackground,
          })}
          icon="angle-down"
        />
        {title}
      </BodyM>

      <section
        className="accordion-content-wrapper"
        id={contentId}
        aria-labelledby={headerId}
        aria-hidden={!isActive}
      >
        <BodyM light={lightText} tag="div" className="accordion-content" html={content} />
      </section>
    </AccordionStyles>
  )
}

export default Accordion
